import { useEffect, useState } from 'react';
import type { ProcessMemoryView } from '../../analytics/memory-analysis.ts';
import type { TransitionDisplayMode } from '../../analytics/memory-states.ts';
import type { SequencePatternRow } from '../../analytics/memory-sequences.ts';
import { DEFAULT_PATTERN_TARGETS } from '../../analytics/memory-core.ts';
import {
  formatDecimal,
  formatHours,
  formatInteger,
  formatPercent,
  formatPercentagePoints,
  formatUtcStamp,
} from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { MemoryLineChart } from '../components/MemoryLineChart.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { TransitionHeatmap } from '../components/TransitionHeatmap.tsx';
import { parseTargetMultiplierInput } from '../../shared/schemas.ts';

type TabId = 'transitions' | 'fingerprints' | 'regimes';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'transitions', label: 'State Transitions' },
  { id: 'fingerprints', label: 'Sequence Fingerprints' },
  { id: 'regimes', label: 'Regimes' },
];

export function MemoryPage() {
  const [tab, setTab] = useState<TabId>('transitions');
  const [view, setView] = useState<ProcessMemoryView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [patternTarget, setPatternTarget] = useState(2);
  const [customTarget, setCustomTarget] = useState('2');
  const [lengths, setLengths] = useState<number[]>([2, 3, 4]);
  const [minSample, setMinSample] = useState(30);
  const [includeHoldout, setIncludeHoldout] = useState(false);
  const [binaryTarget, setBinaryTarget] = useState(2);
  const [loadingHoldout, setLoadingHoldout] = useState(false);
  const [permutationCount, setPermutationCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError(null);
    void fetchJson<ProcessMemoryView>('/api/analytics/memory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patternTarget,
        patternLengths: lengths,
        minPatternSample: minSample,
        binaryTarget,
        includeHoldout,
        permutationCount,
      }),
    })
      .then((value) => {
        if (!cancelled) {
          setView(value);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          const message = cause instanceof Error ? cause.message : 'Failed to load process memory analysis';
          setError(
            message.includes('(404)')
              ? 'The Process Memory page loaded, but POST /api/analytics/memory returned 404. A stale API process is listening on port 3000 without this route. Stop local AviatorAnalytics node processes and run npm run dev, then open http://127.0.0.1:5173/memory — not port 3000 unless that API was started from the current source.'
              : message,
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingHoldout(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [patternTarget, lengths, minSample, includeHoldout, binaryTarget, permutationCount]);

  const evaluateHoldout = () => {
    setLoadingHoldout(true);
    setIncludeHoldout(true);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold">Process Memory</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink">
          Does information from previous rounds contain reproducible information about the distribution of subsequent
          rounds?
        </p>
      </header>

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {!error && !view ? <StatusMessage kind="loading">Loading process memory analysis</StatusMessage> : null}

      {view ? (
        <>
          <DatasetCard view={view} />
          <SummaryCard view={view} />
          {view.permutation === null ? (
            <p className="text-sm">
              <button
                type="button"
                className="rounded border border-ink bg-soft px-3 py-1"
                onClick={() => setPermutationCount(1000)}
              >
                Run 1000-shuffle reference
              </button>
              <span className="ml-2 text-xs text-muted">
                Deferred so the page stays responsive. Uses analytics-owned cache keyed by snapshot identity, parameters, and algorithm version.
              </span>
            </p>
          ) : null}
          <p className="rounded border border-line bg-panel px-3 py-2 text-xs text-muted">{view.permanentNote}</p>
          <nav className="flex flex-wrap gap-2" aria-label="Process memory sections">
            {TABS.map((item) => (
              <ToggleButton key={item.id} active={tab === item.id} label={item.label} onClick={() => setTab(item.id)} />
            ))}
          </nav>
          {tab === 'transitions' ? (
            <TransitionsPanel view={view} binaryTarget={binaryTarget} onBinaryTarget={setBinaryTarget} />
          ) : null}
          {tab === 'fingerprints' ? (
            <FingerprintsPanel
              view={view}
              patternTarget={patternTarget}
              customTarget={customTarget}
              lengths={lengths}
              minSample={minSample}
              loadingHoldout={loadingHoldout}
              onCustomTarget={setCustomTarget}
              onPatternTarget={setPatternTarget}
              onLengths={setLengths}
              onMinSample={setMinSample}
              onEvaluateHoldout={evaluateHoldout}
            />
          ) : null}
          {tab === 'regimes' ? <RegimesPanel view={view} /> : null}
        </>
      ) : null}
    </div>
  );
}

function DatasetCard(props: { view: ProcessMemoryView }) {
  const dataset = props.view.dataset;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Dataset</h2>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Metric label="Dataset rounds" value={formatInteger(dataset.rounds)} />
        <Metric
          label="Elapsed time"
          value={`${formatUtcStamp(dataset.firstTimestampUtc)} – ${formatUtcStamp(dataset.lastTimestampUtc)} (${formatHours(dataset.elapsedHours ?? dataset.periodHours)})`}
        />
        <Metric label="Collected time" value={formatHours(dataset.collectedHours)} />
        <Metric label="Collection coverage" value={formatPercent(dataset.coverage, 1)} />
        <Metric label="Rounds/hour" value={formatDecimal(dataset.roundsPerHour, 2)} />
        <Metric label="Continuity status" value={dataset.integrity} />
        <Metric label="Continuity segments" value={formatInteger(dataset.continuitySegments)} />
        <Metric label="Included recovery statuses" value={dataset.includedRecoveryStatuses.join(', ') || '—'} />
        <Metric label="Snapshot source" value={dataset.snapshotPath ?? dataset.dataLabel} />
        <Metric label="Snapshot timestamp" value={formatUtcStamp(dataset.snapshotTimestampUtc)} />
        <Metric label="Algorithm" value={dataset.algorithmVersion} />
      </dl>
    </section>
  );
}

function SummaryCard(props: { view: ProcessMemoryView }) {
  const summary = props.view.summary;
  const stateLabel =
    summary.stateDependence === 'insufficient_data'
      ? 'Insufficient data'
      : summary.stateDependence === 'evidence_detected'
        ? 'Evidence detected'
        : 'No detectable evidence';
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Scientific summary</h2>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Metric label="State dependence" value={`${stateLabel} (n=${formatInteger(summary.sampleSize)})`} />
        <Metric
          label="Sequence replication"
          value={`${formatInteger(summary.sequenceReplication.discoveryCandidates)} discovery candidates, ${formatInteger(summary.sequenceReplication.replicatedInValidation)} replicated in validation`}
        />
        <Metric
          label="Regime stability"
          value={`${formatInteger(summary.regimeStability.candidateChanges2x)} candidate changes ≥2x, ${formatInteger(summary.regimeStability.candidateChanges5x)} ≥5x`}
        />
        <Metric
          label="Serial dependence"
          value={
            summary.serialDependence.largestAbsoluteAcf === null
              ? '—'
              : `largest |ACF|=${formatDecimal(summary.serialDependence.largestAbsoluteAcf, 3)} at lag ${formatInteger(summary.serialDependence.lag)} (n=${formatInteger(summary.serialDependence.sampleSize)})`
          }
        />
        <Metric
          label="Permutation comparison"
          value={
            summary.permutation.permutations === 0
              ? 'Omitted'
              : `transition statistic percentile ${formatPercent(summary.permutation.transitionStatisticPercentile, 1)} under ${formatInteger(summary.permutation.permutations)} shuffles`
          }
        />
      </dl>
      <p className="mt-3 text-sm text-muted">{summary.stateDependenceWording}</p>
    </section>
  );
}

function TransitionsPanel(props: {
  view: ProcessMemoryView;
  binaryTarget: number;
  onBinaryTarget: (value: number) => void;
}) {
  const [mode, setMode] = useState<TransitionDisplayMode>('observed');
  const independence = props.view.transitions.independence;
  const strongestLag = [...props.view.lags].sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize))[0];
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">State occupancy</h2>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          {props.view.stateCounts.map((row) => (
            <div key={row.state}>
              <span className="font-medium">{row.state}</span>{' '}
              <span className="text-muted">
                {formatInteger(row.count)} ({formatPercent(row.proportion.frequency, 1)}, n={formatInteger(row.proportion.trials)})
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Transition matrix</h2>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['observed', 'Observed probability'],
                ['expected', 'Expected probability'],
                ['difference_pp', 'Difference (pp)'],
                ['oe_ratio', 'Observed/Expected'],
              ] as const
            ).map(([id, label]) => (
              <ToggleButton key={id} active={mode === id} label={label} onClick={() => setMode(id)} />
            ))}
          </div>
        </div>
        <p className="mt-2 text-sm">{props.view.transitions.wording}</p>
        <p className="mt-1 text-xs text-muted">
          χ²={formatDecimal(independence.statistic, 2)}, df={formatInteger(independence.degreesOfFreedom)}, p=
          {formatDecimal(independence.pValue, 4)}, Cramér’s V={formatDecimal(independence.cramersV, 3)}, transitions n=
          {formatInteger(independence.n)}. {independence.approximationNote}
        </p>
        <div className="mt-3">
          <TransitionHeatmap matrix={props.view.transitions} mode={mode} />
        </div>
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Lag dependence (k = 1–20)</h2>
        <p className="mt-1 text-xs text-muted">
          Strongest |Cramér’s V| in this sample: lag {formatInteger(strongestLag?.lag)} V=
          {formatDecimal(strongestLag?.effectSize, 3)} (n={formatInteger(strongestLag?.sampleSize)}). FDR q=0.05 across lags.
        </p>
        <div className="mt-3">
          <MemoryLineChart
            ariaLabel="Cramér's V versus lag"
            series={props.view.lags.map((row) => ({ x: row.lag, y: row.effectSize }))}
            yFormat={(value) => formatDecimal(value, 3)}
          />
        </div>
        <div className="table-scroll mt-3">
          <table className="min-w-[720px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-2 py-2 font-medium">Lag</th>
                <th className="px-2 py-2 font-medium">n</th>
                <th className="px-2 py-2 font-medium">Statistic</th>
                <th className="px-2 py-2 font-medium">Effect size (V)</th>
                <th className="px-2 py-2 font-medium">p</th>
                <th className="px-2 py-2 font-medium">FDR q</th>
              </tr>
            </thead>
            <tbody>
              {props.view.lags.map((row) => (
                <tr key={row.lag} className="border-b border-line/70">
                  <td className="px-2 py-2">{row.lag}</td>
                  <td className="px-2 py-2">{formatInteger(row.sampleSize)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.statistic, 2)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.effectSize, 3)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.pValue, 4)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.qValue, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <BinaryPanel view={props.view} binaryTarget={props.binaryTarget} onBinaryTarget={props.onBinaryTarget} />
    </div>
  );
}

function BinaryPanel(props: {
  view: ProcessMemoryView;
  binaryTarget: number;
  onBinaryTarget: (value: number) => void;
}) {
  const binary = props.view.binary;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Binary threshold process</h2>
        <label className="text-sm">
          M
          <input
            className="ml-2 w-20 rounded border border-line bg-panel px-2 py-1"
            defaultValue={String(props.binaryTarget)}
            onBlur={(event) => {
              try {
                props.onBinaryTarget(parseTargetMultiplierInput(event.target.value));
              } catch {
                event.target.value = String(props.binaryTarget);
              }
            }}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-muted">{binary.note}</p>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Metric
          label={`P(HIT) n=${formatInteger(binary.pHit.trials)}`}
          value={`${formatPercent(binary.pHit.frequency, 2)} (${ci(binary.pHit)})`}
        />
        <Metric
          label={`P(HIT | previous HIT) n=${formatInteger(binary.lag1.pHitGivenPreviousHit.trials)}`}
          value={`${formatPercent(binary.lag1.pHitGivenPreviousHit.frequency, 2)} (${ci(binary.lag1.pHitGivenPreviousHit)}) Δ ${formatPercentagePoints(binary.lag1.differenceVsBaselineGivenHit, 2)}`}
        />
        <Metric
          label={`P(HIT | previous MISS) n=${formatInteger(binary.lag1.pHitGivenPreviousMiss.trials)}`}
          value={`${formatPercent(binary.lag1.pHitGivenPreviousMiss.frequency, 2)} (${ci(binary.lag1.pHitGivenPreviousMiss)}) Δ ${formatPercentagePoints(binary.lag1.differenceVsBaselineGivenMiss, 2)}`}
        />
      </dl>
      <div className="mt-3">
        <MemoryLineChart
          ariaLabel="Binary lag conditional hit rate given previous hit"
          series={binary.lags.map((row) => ({ x: row.lag, y: row.pHitGivenPreviousHit.frequency ?? 0 }))}
          referenceY={binary.pHit.frequency}
          yFormat={(value) => formatPercent(value, 2)}
        />
      </div>
    </section>
  );
}

function FingerprintsPanel(props: {
  view: ProcessMemoryView;
  patternTarget: number;
  customTarget: string;
  lengths: number[];
  minSample: number;
  loadingHoldout: boolean;
  onCustomTarget: (value: string) => void;
  onPatternTarget: (value: number) => void;
  onLengths: (value: number[]) => void;
  onMinSample: (value: number) => void;
  onEvaluateHoldout: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const rows = props.view.fingerprints.rows;
  const selectedRow = rows.find((row) => row.pattern === selected) ?? rows[0] ?? null;
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-panel p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            Target
            <select
              className="ml-2 rounded border border-line bg-panel px-2 py-1"
              value={[2, 5, 10, 50, 100].includes(props.patternTarget) ? String(props.patternTarget) : 'custom'}
              onChange={(event) => {
                if (event.target.value === 'custom') {
                  return;
                }
                props.onPatternTarget(Number(event.target.value));
              }}
            >
              {DEFAULT_PATTERN_TARGETS.map((target) => (
                <option key={target} value={target}>
                  ≥{target}x
                </option>
              ))}
              <option value="custom">custom</option>
            </select>
          </label>
          <label className="text-sm">
            Custom M
            <input
              className="ml-2 w-20 rounded border border-line bg-panel px-2 py-1"
              value={props.customTarget}
              onChange={(event) => props.onCustomTarget(event.target.value)}
              onBlur={() => {
                try {
                  props.onPatternTarget(parseTargetMultiplierInput(props.customTarget));
                } catch {
                  props.onCustomTarget(String(props.patternTarget));
                }
              }}
            />
          </label>
          <fieldset className="text-sm">
            <legend className="text-xs text-muted">Pattern lengths</legend>
            {[2, 3, 4, 5].map((length) => (
              <label key={length} className="ml-2">
                <input
                  type="checkbox"
                  checked={props.lengths.includes(length)}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...props.lengths, length].sort((a, b) => a - b)
                      : props.lengths.filter((value) => value !== length);
                    if (next.length > 0) {
                      props.onLengths(next);
                    }
                  }}
                />{' '}
                {length}
              </label>
            ))}
          </fieldset>
          <label className="text-sm">
            Minimum n
            <input
              type="number"
              min={1}
              className="ml-2 w-20 rounded border border-line bg-panel px-2 py-1"
              value={props.minSample}
              onChange={(event) => props.onMinSample(Math.max(1, Number(event.target.value) || 1))}
            />
          </label>
        </div>
        <p className="mt-2 text-xs text-muted">{props.view.fingerprints.splitNote}</p>
        <p className="mt-1 text-xs text-muted">{props.view.fingerprints.exploratorySortNote}</p>
        <p className="mt-1 text-sm">
          Eligible patterns: length 2 {formatInteger(props.view.fingerprints.eligibleByLength['2'])}, length 3{' '}
          {formatInteger(props.view.fingerprints.eligibleByLength['3'])}, length 4{' '}
          {formatInteger(props.view.fingerprints.eligibleByLength['4'])}. Discovery candidates after FDR:{' '}
          {formatInteger(props.view.fingerprints.discoveryCandidates)}. Replicated in validation:{' '}
          {formatInteger(props.view.fingerprints.replicatedInValidation)}. Holdout:{' '}
          {props.view.fingerprints.holdoutStatus}.
        </p>
        {props.view.fingerprints.holdoutStatus === 'UNTOUCHED' ? (
          <button
            type="button"
            className="mt-3 rounded border border-ink bg-soft px-3 py-1 text-sm"
            onClick={props.onEvaluateHoldout}
            disabled={props.loadingHoldout}
          >
            Evaluate Holdout
          </button>
        ) : (
          <p className="mt-3 text-xs text-muted">Holdout has been evaluated for the frozen discovery candidates. It was not used to select them.</p>
        )}
      </section>

      <section className="rounded border border-line bg-panel">
        <div className="table-scroll">
          <table className="min-w-[1100px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-2 py-2 font-medium">Pattern</th>
                <th className="px-2 py-2 font-medium">Length</th>
                <th className="px-2 py-2 font-medium">Discovery n</th>
                <th className="px-2 py-2 font-medium">Discovery rate</th>
                <th className="px-2 py-2 font-medium">Baseline</th>
                <th className="px-2 py-2 font-medium">Difference</th>
                <th className="px-2 py-2 font-medium">Raw p</th>
                <th className="px-2 py-2 font-medium">FDR q</th>
                <th className="px-2 py-2 font-medium">Validation n</th>
                <th className="px-2 py-2 font-medium">Validation rate</th>
                <th className="px-2 py-2 font-medium">Validation Δ</th>
                <th className="px-2 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.length}-${row.pattern}`}
                  className={`cursor-pointer border-b border-line/70 hover:bg-soft ${selectedRow?.pattern === row.pattern && selectedRow.length === row.length ? 'bg-soft' : ''}`}
                  onClick={() => setSelected(row.pattern)}
                >
                  <td className="px-2 py-2 font-medium">
                    {row.pattern}
                    {row.insufficientSample ? (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-warn">Insufficient sample</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">{row.length}</td>
                  <td className="px-2 py-2">{formatInteger(row.discovery.n)}</td>
                  <td className="px-2 py-2">{formatPercent(row.discovery.conditional.frequency, 1)}</td>
                  <td className="px-2 py-2">{formatPercent(row.discovery.baseline.frequency, 1)}</td>
                  <td className="px-2 py-2">{formatPercentagePoints(row.discovery.difference, 1)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.discovery.rawP, 4)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.fdrQ, 4)}</td>
                  <td className="px-2 py-2">{formatInteger(row.validation.n)}</td>
                  <td className="px-2 py-2">{formatPercent(row.validation.conditional.frequency, 1)}</td>
                  <td className="px-2 py-2">{formatPercentagePoints(row.validation.difference, 1)}</td>
                  <td className="px-2 py-2">{statusLabel(row.replicationStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedRow ? <PatternDetail row={selectedRow} labels={props.view.transitions.labels} /> : null}
    </div>
  );
}

function PatternDetail(props: { row: SequencePatternRow; labels: readonly string[] }) {
  const row = props.row;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Pattern detail {row.pattern}</h2>
      <p className="mt-2 text-sm">
        Discovery n={formatInteger(row.discovery.n)}, conditional {formatPercent(row.discovery.conditional.frequency, 2)}{' '}
        ({ci(row.discovery.conditional)}) vs baseline {formatPercent(row.discovery.baseline.frequency, 2)}, ratio{' '}
        {formatDecimal(row.discovery.relativeRatio, 2)}. Validation n={formatInteger(row.validation.n)}, rate{' '}
        {formatPercent(row.validation.conditional.frequency, 2)} ({ci(row.validation.conditional)}), p=
        {formatDecimal(row.validation.rawP, 4)}.
        {row.holdout
          ? ` Holdout n=${formatInteger(row.holdout.n)}, rate ${formatPercent(row.holdout.conditional.frequency, 2)}.`
          : ' Holdout unevaluated.'}
      </p>
      <div className="mt-3 flex flex-wrap gap-3 text-sm">
        {props.labels.map((label) => (
          <span key={label}>
            next {label}: {formatInteger(row.nextStateCountsDiscovery[label] ?? 0)}
          </span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-sm">
        {Object.entries(row.nextHitRatesDiscovery).map(([target, proportion]) => (
          <span key={target}>
            next ≥{target}x: {formatPercent(proportion.frequency, 1)} (n={formatInteger(proportion.trials)})
          </span>
        ))}
      </div>
    </section>
  );
}

function RegimesPanel(props: { view: ProcessMemoryView }) {
  const [windowSize, setWindowSize] = useState(100);
  const [blockSize, setBlockSize] = useState(500);
  const [xMode, setXMode] = useState<'sequence' | 'utc'>('sequence');
  const rolling = props.view.rolling.find((series) => series.windowSize === windowSize) ?? props.view.rolling[0];
  const blocks = props.view.blocks[String(blockSize)] ?? [];
  const hit2Baseline = blocks.length
    ? blocks.reduce((sum, row) => sum + (row.hit2.frequency ?? 0) * row.n, 0) /
      Math.max(1, blocks.reduce((sum, row) => sum + row.n, 0))
    : null;
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Rolling distribution</h2>
          <div className="flex flex-wrap gap-2">
            {[50, 100, 250, 500].map((size) => (
              <ToggleButton key={size} active={windowSize === size} label={`${size}`} onClick={() => setWindowSize(size)} />
            ))}
            <ToggleButton active={xMode === 'sequence'} label="Sequence" onClick={() => setXMode('sequence')} />
            <ToggleButton active={xMode === 'utc'} label="UTC timestamp" onClick={() => setXMode('utc')} />
          </div>
        </div>
        <p className="mt-2 text-xs text-muted">{props.view.trimmedMeanNote}</p>
        {rolling ? (
          <div className="mt-3 space-y-4">
            <MemoryLineChart
              ariaLabel="Rolling >=2x frequency"
              series={rolling.points.map((point) => ({
                x: xMode === 'sequence' ? point.endSequence : Date.parse(point.endTimestampUtc),
                y: point.hit2.frequency ?? 0,
              }))}
              yFormat={(value) => formatPercent(value, 2)}
            />
            <MemoryLineChart
              ariaLabel="Rolling median multiplier"
              series={rolling.points.map((point) => ({
                x: xMode === 'sequence' ? point.endSequence : Date.parse(point.endTimestampUtc),
                y: point.median ?? 0,
              }))}
            />
          </div>
        ) : null}
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Fixed blocks</h2>
          <div className="flex gap-2">
            {[250, 500, 1000].map((size) => (
              <ToggleButton key={size} active={blockSize === size} label={`${size}`} onClick={() => setBlockSize(size)} />
            ))}
          </div>
        </div>
        <div className="mt-3">
          <MemoryLineChart
            ariaLabel="Block >=2x hit rate"
            series={blocks.map((row) => ({ x: row.block, y: row.hit2.frequency ?? 0 }))}
            referenceY={hit2Baseline}
            yFormat={(value) => formatPercent(value, 2)}
          />
        </div>
        <div className="table-scroll mt-3">
          <table className="min-w-[860px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-2 py-2 font-medium">Block</th>
                <th className="px-2 py-2 font-medium">Rounds</th>
                <th className="px-2 py-2 font-medium">Time</th>
                <th className="px-2 py-2 font-medium">n</th>
                <th className="px-2 py-2 font-medium">≥2x</th>
                <th className="px-2 py-2 font-medium">95% CI</th>
                <th className="px-2 py-2 font-medium">Median</th>
                <th className="px-2 py-2 font-medium">Trimmed mean</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((row) => (
                <tr key={row.block} className="border-b border-line/70">
                  <td className="px-2 py-2">
                    {row.block}
                    {row.partial ? <span className="ml-1 text-[10px] uppercase text-muted">partial</span> : null}
                  </td>
                  <td className="px-2 py-2">
                    {formatInteger(row.startSequence)}–{formatInteger(row.endSequence)}
                  </td>
                  <td className="px-2 py-2">
                    {formatUtcStamp(row.startTimestampUtc)} – {formatUtcStamp(row.endTimestampUtc)}
                  </td>
                  <td className="px-2 py-2">{formatInteger(row.n)}</td>
                  <td className="px-2 py-2">{formatPercent(row.hit2.frequency, 2)}</td>
                  <td className="px-2 py-2">{ci(row.hit2)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.median, 2)}</td>
                  <td className="px-2 py-2">{formatDecimal(row.trimmedMean, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ChangePointTable view={props.view} />
      <AutocorrelationPanel view={props.view} />
    </div>
  );
}

function ChangePointTable(props: { view: ProcessMemoryView }) {
  const docs = props.view.changePoints.documentation;
  const rows = [...props.view.changePoints.binary2, ...props.view.changePoints.binary5];
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Candidate change points</h2>
      <p className="mt-2 text-xs text-muted">
        {docs.algorithm} {docs.objective} {docs.tuning}
      </p>
      <p className="mt-1 text-xs text-muted">{docs.limitations}</p>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm">No candidate change points were detected with the conservative defaults.</p>
      ) : (
        <div className="table-scroll mt-3">
          <table className="min-w-[860px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-2 py-2 font-medium">Metric</th>
                <th className="px-2 py-2 font-medium">Sequence</th>
                <th className="px-2 py-2 font-medium">Timestamp</th>
                <th className="px-2 py-2 font-medium">Pre n</th>
                <th className="px-2 py-2 font-medium">Post n</th>
                <th className="px-2 py-2 font-medium">Pre</th>
                <th className="px-2 py-2 font-medium">Post</th>
                <th className="px-2 py-2 font-medium">Difference</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.metric}-${row.sequence}`} className="border-b border-line/70">
                  <td className="px-2 py-2">{row.metric === 'binary_ge_2' ? '≥2x' : '≥5x'}</td>
                  <td className="px-2 py-2">{formatInteger(row.sequence)}</td>
                  <td className="px-2 py-2">{formatUtcStamp(row.timestampUtc)}</td>
                  <td className="px-2 py-2">{formatInteger(row.preN)}</td>
                  <td className="px-2 py-2">{formatInteger(row.postN)}</td>
                  <td className="px-2 py-2">{formatPercent(row.preRate.frequency, 2)}</td>
                  <td className="px-2 py-2">{formatPercent(row.postRate.frequency, 2)}</td>
                  <td className="px-2 py-2">{formatPercentagePoints(row.difference, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AutocorrelationPanel(props: { view: ProcessMemoryView }) {
  const binary = props.view.autocorrelationBinary;
  const logSeries = props.view.autocorrelationLog;
  const perm = props.view.permutation;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Autocorrelation and shuffle reference</h2>
      <p className="mt-2 text-xs text-muted">{binary.bartlettNote}</p>
      <div className="mt-3">
        <MemoryLineChart
          ariaLabel="Binary autocorrelation"
          series={binary.lags.map((row) => ({ x: row.lag, y: row.autocorrelation ?? 0 }))}
          referenceY={0}
          yFormat={(value) => formatDecimal(value, 3)}
        />
      </div>
      {logSeries.omittedReason ? (
        <p className="mt-2 text-xs text-muted">{logSeries.omittedReason}</p>
      ) : (
        <div className="mt-3">
          <MemoryLineChart
            ariaLabel="Log multiplier autocorrelation"
            series={logSeries.lags.map((row) => ({ x: row.lag, y: row.autocorrelation ?? 0 }))}
            referenceY={0}
            yFormat={(value) => formatDecimal(value, 3)}
          />
        </div>
      )}
      {perm ? (
        <p className="mt-3 text-sm">
          {perm.algorithm} Observed max |lag V| percentile {formatPercent(perm.percentiles.maxAbsLagEffect, 1)};
          transition χ² percentile {formatPercent(perm.percentiles.transitionStatistic, 1)}; max |ACF| percentile{' '}
          {formatPercent(perm.percentiles.maxAbsBinaryAcf, 1)}; max eligible pattern effect percentile{' '}
          {formatPercent(perm.percentiles.maxEligiblePatternEffect, 1)}. {perm.note}
        </p>
      ) : (
        <p className="mt-3 text-sm text-muted">{props.view.permutationOmittedReason}</p>
      )}
    </section>
  );
}

function statusLabel(status: SequencePatternRow['replicationStatus']): string {
  if (status === 'INSUFFICIENT_SAMPLE') {
    return 'Insufficient sample';
  }
  if (status === 'INSUFFICIENT_EVIDENCE') {
    return 'Insufficient evidence';
  }
  if (status === 'NOT_REPLICATED') {
    return 'Not replicated';
  }
  if (status === 'REPLICATED') {
    return 'Replicated';
  }
  return 'Candidate';
}

function ci(proportion: { confidenceInterval: { lower: number; upper: number } | null }): string {
  if (!proportion.confidenceInterval) {
    return 'CI omitted';
  }
  return `${formatPercent(proportion.confidenceInterval.lower, 1)}–${formatPercent(proportion.confidenceInterval.upper, 1)}`;
}

function ToggleButton(props: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`rounded border px-2 py-1 text-sm ${
        props.active ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
      }`}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-sm font-medium break-all">{props.value}</dd>
    </div>
  );
}
